import type { DealStatus } from "@/api/client";
import { Badge } from "@/components/ui/badge";

/**
 * Status badge for the Manager Pipeline Ledger (CHP-222, RACCPE-20).
 *
 * The CM/RA/RM ledger uses its own status vocabulary — Live / Declined /
 * Closed-Won / Closed-Lost — distinct from the partner-side shared
 * `DealStatusBadge` (which a partner reads from their own vantage,
 * e.g. "Registration in review", "Extension in review"). CHP-411 split the
 * three review-pending states (previously all "To be reviewed") into
 * per-review-type labels so the manager can tell at a glance which kind of
 * review each row needs; CHP-299 set the column copy to the AC's exact
 * "Deal <thing> in review" wording. The feed-card kind labels above the
 * ledger keep their own shorter CHP-411 strings (ManagerFeedSidebar) —
 * deliberately not shared with this map.
 */

/** The CM/RA/RM ledger status labels. Exported so the PO ledger's sortable
 * Status header (CHP-417) can sort on the same text the badge shows. */
export const MANAGER_DEAL_STATUS_LABEL: Record<DealStatus, string> = {
  draft: "Draft",
  pending_review: "Deal registration in review",
  extension_pending: "Deal extension in review",
  win_pending_review: "Closed-won status in review",
  live: "Live",
  denied: "Declined",
  closed_won: "Closed-Won",
  closed_lost: "Closed-Lost",
};

const COPY = MANAGER_DEAL_STATUS_LABEL;

const VARIANT: Record<DealStatus, "default" | "outline" | "secondary" | "destructive" | "success"> =
  {
    draft: "outline",
    pending_review: "secondary",
    extension_pending: "secondary",
    win_pending_review: "secondary",
    live: "success",
    denied: "destructive",
    closed_won: "success",
    closed_lost: "destructive",
  };

export function ManagerDealStatusBadge({ status }: { status: DealStatus }) {
  return <Badge variant={VARIANT[status]}>{COPY[status]}</Badge>;
}
